import { useAssetClassColors } from '@containers/AssetClassColors/useAssetClassColors';
import type { AssetClassLevel } from '@generated/graphql';
import type {
  AssetClassBranch,
  FinancialProduct,
} from '@interfaces/financialProduct';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { Box, Typography } from 'hephaestus/web';
import { round } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
  type DroppableStateSnapshot,
  type DropResult,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import {
  applyWithinClassAfterPortfolioRowEdits,
  buildClassTargetByProductId,
  buildReconciledPortfolioShareByProductId,
  formatPortfolioPercentage,
  portfolioDraftValuesEqual,
  resolvePortfolioPercentage,
  sumPortfolioPercentages,
  sumTargetPercentages,
} from './fundTargetPercentage';
import {
  GroupedTableBody,
  TableHeaders,
  targetPercentagePaddingRight,
} from './groupedTableBody';
import { SecurityRow } from './securityRow';
import type { DragProps, Security, SecurityTableOptions } from './types';
import {
  findSecurityIndex,
  isPortfolioPercentageColumnEnabled,
  TABLE_MIN_WIDTH,
} from './types';

export type {
  ColumnConfig,
  Security,
  SecurityTableColumns,
  SecurityTableOptions,
} from './types';

/** Swaps tax ranks between `index` and whoever holds `newRank`; returns `null` if `newRank` is free (use `onUpdateTaxRank`). Positions unchanged. */
export const computeTaxRankSwap = (
  securities: Security[],
  index: number,
  newRank: number,
): Security[] | null => {
  const existingIndex = securities.findIndex((s) => s.taxRank === newRank);
  if (existingIndex === -1 || existingIndex === index) return null;

  const updated = [...securities];
  const current = { ...updated[index] };
  const existing = { ...updated[existingIndex] };
  updated[index] = { ...current, taxRank: newRank };
  updated[existingIndex] = { ...existing, taxRank: current.taxRank };
  return updated;
};

type SecuritiesSelectionTableProps = {
  securities: Security[];
  options?: SecurityTableOptions;
  onRemoveSecurity?: (index: number) => void;
  onAddSecurity?: () => void;
  onAddCash?: () => void;
  onUpdateSecurity?: (index: number, newSecurity: Security) => void;
  onUpdateTaxRank?: (index: number, value: number) => void;
  onUpdateTargetPercentage?: (index: number, value: number) => void;
  onReorderSecurities?: (securities: Security[]) => void;
  ignoreBorderStyling?: boolean;
  editable?: boolean;
  sortable?: boolean;
  /** Highlights total row when not 100% after a submit attempt */
  submitAttempted?: boolean;
  /** ASSET_CLASS: render grouped sections (one per branch) */
  groupedByAssetClass?: Array<{
    branch: AssetClassBranch;
    securities: Security[];
  }>;
  /** Securities with no matching branch */
  unassignedSecurities?: Security[];
  /** Passed to `SecuritySelect` to group the dropdown by primary/secondary/tertiary asset class */
  securitySelectAssetClassLevel?: AssetClassLevel | string;
};

export const SecuritiesSelectionTable = ({
  securities: initialSecurities = [],
  options,
  onRemoveSecurity,
  onAddSecurity,
  onAddCash,
  onUpdateSecurity,
  onUpdateTaxRank,
  onUpdateTargetPercentage,
  onReorderSecurities,
  ignoreBorderStyling = false,
  editable = true,
  sortable = false,
  submitAttempted = false,
  groupedByAssetClass,
  unassignedSecurities = [],
  securitySelectAssetClassLevel,
}: SecuritiesSelectionTableProps) => {
  const { translateBackendString } = useLocalization();
  const isGroupedMode = !!groupedByAssetClass;
  const portfolioPercentageEnabled =
    isGroupedMode && isPortfolioPercentageColumnEnabled(options);
  const showPortfolioPercentageColumn = portfolioPercentageEnabled;
  const classTargetByProductId = useMemo(
    () => buildClassTargetByProductId(groupedByAssetClass),
    [groupedByAssetClass],
  );
  const reconciledPortfolioShareByProductId = useMemo(
    () => buildReconciledPortfolioShareByProductId(groupedByAssetClass),
    [groupedByAssetClass],
  );
  const displayedPortfolioShare = useCallback(
    (security: Security | undefined, classTarget?: number | null) => {
      if (!security) return null;
      return (
        reconciledPortfolioShareByProductId.get(
          security.financialProduct.id ?? '',
        ) ??
        resolvePortfolioPercentage(
          security,
          classTarget ??
            classTargetByProductId.get(security.financialProduct.id ?? ''),
        )
      );
    },
    [reconciledPortfolioShareByProductId, classTargetByProductId],
  );
  const cashAlreadyExists = initialSecurities.some(
    (s) => s.financialProduct.isCash,
  );
  const { t } = useTranslation(['shared', 'modelPortfolios', 'components']);
  const [securityEditingIndex, setSecurityEditingIndex] = useState<
    number | null
  >(null);
  const [targetPercentageEditingIndex, setTargetPercentageEditingIndex] =
    useState<number | null>(null);
  const targetPercentageEditingIndexRef = useRef<number | null>(null);
  const [portfolioPercentageEditingIndex, setPortfolioPercentageEditingIndex] =
    useState<number | null>(null);
  const portfolioPercentageEditingIndexRef = useRef<number | null>(null);
  const [taxRankEditingIndex, setTaxRankEditingIndex] = useState<number | null>(
    null,
  );
  const [currentTaxRankValue, setCurrentTaxRankValue] = useState<string>('');
  const [sortField, setSortField] = useState<string>('taxRank');
  const [sortDesc, setSortDesc] = useState<boolean>(false);
  const selectSecurityRef = useRef<HTMLDivElement>(null);
  const selectTargetPercentageRef = useRef<HTMLDivElement>(null);
  const selectPortfolioPercentageRef = useRef<HTMLDivElement>(null);
  const selectTaxRankRef = useRef<HTMLDivElement>(null);
  const exceptTickers = useMemo(
    () =>
      initialSecurities
        .map((security) => security.financialProduct.ticker || '')
        .filter((ticker) => ticker !== ''),
    [initialSecurities],
  );
  const [securitiesPercentage, setSecuritiesPercentage] = useState<string[]>(
    [],
  );
  const [securitiesPortfolioPercentage, setSecuritiesPortfolioPercentage] =
    useState<string[]>([]);

  const getNestedValue = (obj: unknown, path: string): unknown => {
    const parts = path.split('.');
    let current: unknown = obj;

    for (const part of parts) {
      if (current === undefined || current === null) return undefined;
      if (typeof current !== 'object') return undefined;

      const rec = current as Record<string, unknown>;
      if (part === 'name' && rec.translatedName) {
        current = translateBackendString(rec.translatedName) || rec.name;
      } else {
        current = rec[part];
      }
    }

    return current;
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(false);
    }
  };

  const sortByField = useCallback(
    (a: Security, b: Security) => {
      const aValue = getNestedValue(a, sortField);
      const bValue = getNestedValue(b, sortField);

      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return sortDesc ? -comparison : comparison;
    },
    [sortField, sortDesc],
  );

  const sortByAssetClass = useCallback((a: Security, b: Security) => {
    const getAssetClassName = (security: Security) =>
      translateBackendString(
        security.financialProduct.primaryAssetClass?.translatedName,
      ) ||
      security.financialProduct.primaryAssetClass?.name ||
      '';

    return getAssetClassName(a).localeCompare(getAssetClassName(b));
  }, []);

  const separateCashSecurities = useCallback(
    (securitiesList: Security[]) => ({
      cashSecurities: securitiesList.filter(
        (security) => security.financialProduct.isCash,
      ),
      nonCashSecurities: securitiesList.filter(
        (security) => !security.financialProduct.isCash,
      ),
    }),
    [],
  );

  const sortedSecurities = useMemo(() => {
    if (editable) {
      return initialSecurities;
    }

    const { cashSecurities, nonCashSecurities } =
      separateCashSecurities(initialSecurities);

    const sortedNonCashSecurities = sortable
      ? [...nonCashSecurities].sort(sortByField)
      : [...nonCashSecurities].sort(sortByAssetClass);

    return [...sortedNonCashSecurities, ...cashSecurities];
  }, [
    initialSecurities,
    editable,
    sortable,
    sortByField,
    sortByAssetClass,
    separateCashSecurities,
  ]);

  useEffect(() => {
    setSecuritiesPercentage((prev) =>
      sortedSecurities.map((security, i) => {
        if (targetPercentageEditingIndexRef.current === i && i < prev.length) {
          return prev[i];
        }
        return security.percentage?.toString() ?? '';
      }),
    );
  }, [sortedSecurities]);

  useEffect(() => {
    setSecuritiesPortfolioPercentage((prev) =>
      sortedSecurities.map((security, i) => {
        if (
          portfolioPercentageEditingIndexRef.current === i &&
          i < prev.length
        ) {
          return prev[i];
        }

        return displayedPortfolioShare(security)?.toString() ?? '';
      }),
    );
  }, [sortedSecurities, displayedPortfolioShare]);

  const portfolioPercentageDraftRef = useRef<string[]>([]);
  const portfolioPercentageFocusSnapshotRef = useRef<{
    index: number;
    value: string;
  } | null>(null);

  useEffect(() => {
    portfolioPercentageDraftRef.current = securitiesPortfolioPercentage;
  }, [securitiesPortfolioPercentage]);

  const securitiesStringPercentage = useMemo(
    () =>
      sortedSecurities.map((security) => security.percentage?.toString() ?? ''),
    [sortedSecurities],
  );

  const handleSecurityUpdate = (
    index: number,
    newSecurity: FinancialProduct,
  ) => {
    if (newSecurity && onUpdateSecurity) {
      const row = initialSecurities[index];
      const productChanged = row.financialProduct?.id !== newSecurity.id;
      const updatedSecurity: Security = {
        ...row,
        financialProduct: {
          ...newSecurity,
          name:
            translateBackendString(newSecurity.translatedName) ||
            newSecurity.name,
          translatedName: newSecurity.translatedName,
        },
        // Replacing the fund keeps the old row spread; drop sleeve so grouping
        // and portfolio % follow the new product tag, not the previous group.
        ...(productChanged ? { assetClass: undefined } : {}),
      };

      onUpdateSecurity(index, updatedSecurity);
      setSecurityEditingIndex(null);
    }
  };

  const handleTaxRankUpdate = useCallback(
    (index: number, value: number) => {
      if (!value || value <= 0 || !Number.isFinite(value)) return;

      // Ranks are global on the flat children array despite grouped UI.
      const swapped = computeTaxRankSwap(initialSecurities, index, value);
      if (swapped) {
        onReorderSecurities?.(swapped);
      } else {
        onUpdateTaxRank?.(index, value);
      }
    },
    [initialSecurities, onUpdateTaxRank, onReorderSecurities],
  );

  const handleTaxRankFocus = (index: number) => {
    setTaxRankEditingIndex(index);
    setCurrentTaxRankValue(initialSecurities[index].taxRank?.toString() || '');
  };

  const handleTaxRankBlur = useCallback(
    (index: number) => {
      handleTaxRankUpdate(index, Number(currentTaxRankValue));
      setTaxRankEditingIndex(null);
      setCurrentTaxRankValue('');
    },
    [currentTaxRankValue, handleTaxRankUpdate],
  );

  const handleTargetPercentageUpdate = (index: number, value: number) => {
    if (!Number.isFinite(value)) return;

    onUpdateTargetPercentage?.(index, value);
  };

  const handleAddSecurity = () => {
    if (onAddSecurity) {
      setSecurityEditingIndex(initialSecurities.length);
      setTaxRankEditingIndex(null);
      targetPercentageEditingIndexRef.current = initialSecurities.length;
      setTargetPercentageEditingIndex(initialSecurities.length);
      onAddSecurity();
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onReorderSecurities) return;

    const { source, destination } = result;
    if (
      source.index === destination.index &&
      source.droppableId === destination.droppableId
    )
      return;

    // Grouped mode: `flatItems` maps draggable index → group (one Droppable, concatenated groups).
    if (isGroupedMode && groupedByAssetClass) {
      const flatItems = groupedByAssetClass.flatMap(
        ({ branch, securities: groupSecs }) =>
          groupSecs.map((sec) => ({ sec, branch })),
      );

      const sourceItem = flatItems[source.index];
      const destItem = flatItems[destination.index];
      if (!sourceItem || !destItem) return;

      const sourceGroupId =
        sourceItem.branch.assetClass?.id ?? sourceItem.branch.id;
      const destGroupId = destItem.branch.assetClass?.id ?? destItem.branch.id;
      if (sourceGroupId !== destGroupId) return; // prevent cross-group drops

      const group = groupedByAssetClass.find(
        (g) => (g.branch.assetClass?.id ?? g.branch.id) === sourceGroupId,
      );
      if (!group) return;

      const groupFlatStart = flatItems.findIndex(
        (item) =>
          (item.branch.assetClass?.id ?? item.branch.id) === sourceGroupId,
      );
      const localSourceIndex = source.index - groupFlatStart;
      const localDestIndex = destination.index - groupFlatStart;

      const groupSecs = [...group.securities];
      const [moved] = groupSecs.splice(localSourceIndex, 1);
      groupSecs.splice(localDestIndex, 0, moved);

      const groupIds = new Set(
        group.securities.map((s) => s.financialProduct.id),
      );
      let groupSecIdx = 0;
      const updatedSecurities = initialSecurities.map((sec) =>
        groupIds.has(sec.financialProduct.id) ? groupSecs[groupSecIdx++] : sec,
      );

      // Keep the multiset of ranks (e.g. 1,2,4), do not renumber to 1,2,3.
      const groupGlobalIndices = new Set(
        groupSecs.map((sec) => findSecurityIndex(updatedSecurities, sec)),
      );
      const groupOriginalRanks = group.securities
        .filter((s) => !s.financialProduct.isCash)
        .map((sec) => {
          const i = findSecurityIndex(initialSecurities, sec);
          return i >= 0 ? (sec.taxRank ?? i + 1) : undefined;
        })
        .filter((r): r is number => r !== undefined)
        .sort((a, b) => a - b);

      let groupRankIndex = 0;
      const finalSecurities = updatedSecurities.map((security, idx) => {
        if (!groupGlobalIndices.has(idx)) return security;
        return {
          ...security,
          taxRank: security.financialProduct.isCash
            ? undefined
            : groupOriginalRanks[groupRankIndex++],
        };
      });

      onReorderSecurities(finalSecurities);
      return;
    }

    const sourceIndex = source.index;
    const destinationIndex = destination.index;
    const updatedSecurities = [...initialSecurities];

    const lastCashIndex =
      updatedSecurities.length -
      1 -
      [...updatedSecurities]
        .reverse()
        .findIndex((security) => security.financialProduct.isCash);

    if (lastCashIndex !== -1 && destinationIndex > lastCashIndex) return;
    if (updatedSecurities[sourceIndex].financialProduct.isCash) return;

    const [movedSecurity] = updatedSecurities.splice(sourceIndex, 1);
    updatedSecurities.splice(destinationIndex, 0, movedSecurity);

    // Same rank multiset rule as grouped mode; only the moved range is updated.
    const minIdx = Math.min(sourceIndex, destinationIndex);
    const maxIdx = Math.max(sourceIndex, destinationIndex);

    const rangeOriginalRanks = initialSecurities
      .slice(minIdx, maxIdx + 1)
      .filter((s) => !s.financialProduct.isCash)
      .map((s, i) => s.taxRank ?? minIdx + i + 1)
      .sort((a, b) => a - b);

    let rangeRankIndex = 0;
    const finalSecurities = updatedSecurities.map((security, index) => {
      if (security.financialProduct.isCash) {
        return { ...security, taxRank: undefined };
      }
      if (index < minIdx || index > maxIdx) {
        return security;
      }
      return {
        ...security,
        taxRank: rangeOriginalRanks[rangeRankIndex++],
      };
    });

    setTimeout(() => {
      onReorderSecurities(finalSecurities);
    }, 0);
  };

  const handleTargetPercentageFocus = (index: number) => {
    portfolioPercentageFocusSnapshotRef.current = null;
    portfolioPercentageEditingIndexRef.current = null;
    setPortfolioPercentageEditingIndex(null);
    targetPercentageEditingIndexRef.current = index;
    setTargetPercentageEditingIndex(index);
  };

  const handlePortfolioPercentageFocus = (index: number) => {
    targetPercentageEditingIndexRef.current = null;
    setTargetPercentageEditingIndex(null);
    portfolioPercentageEditingIndexRef.current = index;
    setPortfolioPercentageEditingIndex(index);

    const portfolioShare = displayedPortfolioShare(initialSecurities[index]);
    const snapshotValue = formatPortfolioPercentage(portfolioShare);
    portfolioPercentageFocusSnapshotRef.current = {
      index,
      value: snapshotValue,
    };

    if (snapshotValue !== '') {
      setSecuritiesPortfolioPercentage((prev) => {
        const next = [...prev];
        next[index] = snapshotValue;
        return next;
      });
    }
  };

  const resetTargetPercentagePreview = useCallback(
    (index: number) => {
      setSecuritiesPercentage((prev) => {
        const next = [...prev];
        next[index] = sortedSecurities[index]?.percentage?.toString() ?? '';
        return next;
      });
    },
    [sortedSecurities],
  );

  const resetPortfolioPercentageDraft = useCallback(
    (index: number) => {
      const portfolioShare = displayedPortfolioShare(initialSecurities[index]);

      setSecuritiesPortfolioPercentage((prev) => {
        const next = [...prev];
        next[index] = portfolioShare?.toString() ?? '';
        return next;
      });
    },
    [displayedPortfolioShare, initialSecurities],
  );

  const applyPortfolioPercentageToRow = useCallback(
    (editedIndex: number, editedPortfolioValue: string): boolean => {
      const portfolioShare = Number(editedPortfolioValue);
      if (!Number.isFinite(portfolioShare)) return false;

      if (!groupedByAssetClass) return false;

      const editedProductId =
        initialSecurities[editedIndex]?.financialProduct.id ?? '';
      const group = groupedByAssetClass.find(({ securities: groupSecs }) =>
        groupSecs.some((sec) => sec.financialProduct.id === editedProductId),
      );
      if (!group) return false;

      const classTarget = group.branch.targetPercentage;
      if (
        classTarget == null ||
        !Number.isFinite(classTarget) ||
        classTarget === 0
      ) {
        return false;
      }

      const editedGroupIndex = group.securities.findIndex(
        (sec) => sec.financialProduct.id === editedProductId,
      );
      if (editedGroupIndex < 0) return false;

      const portfolioDraft = portfolioPercentageDraftRef.current;
      const portfolioShares = group.securities.map((sec) => {
        const globalIndex = findSecurityIndex(initialSecurities, sec);
        if (globalIndex === editedIndex) {
          return portfolioShare;
        }

        const draftValue = portfolioDraft[globalIndex];
        const draftNumber = Number(draftValue);
        if (Number.isFinite(draftNumber)) {
          return draftNumber;
        }

        return (
          displayedPortfolioShare(sec, classTarget) ??
          Number(sec.percentage ?? 0)
        );
      });

      const storedWithinClassTargets = group.securities.map(
        (sec) => sec.percentage ?? 0,
      );

      const nextWithinClassTargets = applyWithinClassAfterPortfolioRowEdits(
        storedWithinClassTargets,
        portfolioShares,
        classTarget,
        [editedGroupIndex],
      );

      const updates: Array<{ globalIndex: number; value: number }> = [];
      group.securities.forEach((sec, groupIndex) => {
        const globalIndex = findSecurityIndex(initialSecurities, sec);
        const nextTarget = nextWithinClassTargets[groupIndex];
        const previousTarget = sec.percentage ?? 0;

        if (round(nextTarget, 2) === round(previousTarget, 2)) {
          return;
        }

        updates.push({ globalIndex, value: nextTarget });
      });

      if (updates.length === 0) {
        return false;
      }

      const committedPortfolioDisplay =
        formatPortfolioPercentage(portfolioShare);

      updates.forEach(({ globalIndex, value }) => {
        if (!Number.isFinite(value)) {
          return;
        }
        handleTargetPercentageUpdate(globalIndex, value);
      });

      setSecuritiesPercentage((prev) => {
        const updated = [...prev];
        updates.forEach(({ globalIndex, value }) => {
          updated[globalIndex] = value.toString();
        });
        return updated;
      });

      setSecuritiesPortfolioPercentage((prev) => {
        const updated = [...prev];
        updated[editedIndex] = committedPortfolioDisplay;
        return updated;
      });

      return true;
    },
    [displayedPortfolioShare, groupedByAssetClass, initialSecurities],
  );

  const closePortfolioPercentageEdit = useCallback(
    (index: number, portfolioValue: string) => {
      // Blur and document mousedown both close; ignore duplicate closes.
      if (portfolioPercentageEditingIndexRef.current !== index) {
        return;
      }

      const focusSnapshot = portfolioPercentageFocusSnapshotRef.current;
      portfolioPercentageFocusSnapshotRef.current = null;

      if (portfolioValue === '' || !Number.isFinite(Number(portfolioValue))) {
        resetTargetPercentagePreview(index);
        resetPortfolioPercentageDraft(index);
      } else if (
        focusSnapshot?.index === index &&
        portfolioDraftValuesEqual(portfolioValue, focusSnapshot.value)
      ) {
        // Focus/blur without edits must not re-derive target % from rounded portfolio %.
      } else {
        applyPortfolioPercentageToRow(index, portfolioValue);
      }

      portfolioPercentageEditingIndexRef.current = null;
      setPortfolioPercentageEditingIndex(null);
    },
    [
      applyPortfolioPercentageToRow,
      resetPortfolioPercentageDraft,
      resetTargetPercentagePreview,
    ],
  );

  const focusNextRowTaxRank = (fromIndex: number) => {
    for (let i = fromIndex + 1; i < initialSecurities.length; i += 1) {
      if (!initialSecurities[i].financialProduct.isCash) {
        handleTaxRankFocus(i);
        return;
      }
    }
  };

  const handlePortfolioPercentageBlur = useCallback(
    (index: number, portfolioValue: string) => {
      closePortfolioPercentageEdit(index, portfolioValue);
    },
    [closePortfolioPercentageEdit],
  );

  const handlePortfolioPercentageKeyDown = (
    index: number,
    portfolioValue: string,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      closePortfolioPercentageEdit(index, portfolioValue);
    } else if (e.key === 'Tab') {
      closePortfolioPercentageEdit(index, portfolioValue);
      focusNextRowTaxRank(index);
      e.preventDefault();
    }
  };

  const handleTaxRankKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      handleTaxRankBlur(index);
    } else if (e.key === 'Tab') {
      handleTaxRankBlur(index);
      handleTargetPercentageFocus(index);
      e.preventDefault();
    }
  };

  const handleTargetPercentageKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      targetPercentageEditingIndexRef.current = null;
      setTargetPercentageEditingIndex(null);
    } else if (e.key === 'Tab') {
      targetPercentageEditingIndexRef.current = null;
      setTargetPercentageEditingIndex(null);
      if (showPortfolioPercentageColumn) {
        handlePortfolioPercentageFocus(index);
      } else {
        focusNextRowTaxRank(index);
      }
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const numberFieldElements =
        document.querySelectorAll('.MuiInputBase-root');

      const isClickOnNumberField = Array.from(numberFieldElements).some((el) =>
        el.contains(target),
      );
      if (isClickOnNumberField) return;

      if (securityEditingIndex !== null) {
        // Asked of the menu the click is in, rather than of the first menu in
        // the document. A page can hold more than one — the menu a workflow is
        // opened from keeps its own in place the whole time it is open — and
        // the first one is rarely the one being picked from, which reads a
        // click on a security as a click away from it and closes the editor
        // before the pick can land.
        const clickedInAMenu =
          target instanceof Element && !!target.closest('.MuiMenu-paper');
        const isClickInside =
          selectSecurityRef.current?.contains(target) || clickedInAMenu;
        if (!isClickInside) {
          setSecurityEditingIndex(null);
        }
      }

      if (taxRankEditingIndex !== null) {
        if (!selectTaxRankRef.current?.contains(target)) {
          handleTaxRankBlur(taxRankEditingIndex);
        }
      }

      if (targetPercentageEditingIndex !== null) {
        if (!selectTargetPercentageRef.current?.contains(target)) {
          targetPercentageEditingIndexRef.current = null;
          setTargetPercentageEditingIndex(null);
        }
      }

      if (portfolioPercentageEditingIndex !== null) {
        if (!selectPortfolioPercentageRef.current?.contains(target)) {
          handlePortfolioPercentageBlur(
            portfolioPercentageEditingIndex,
            portfolioPercentageDraftRef.current[
              portfolioPercentageEditingIndex
            ] ?? '',
          );
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    securityEditingIndex,
    taxRankEditingIndex,
    targetPercentageEditingIndex,
    portfolioPercentageEditingIndex,
    handleTaxRankBlur,
    handlePortfolioPercentageBlur,
  ]);

  const totalPercentage = useMemo(
    () => sumTargetPercentages(initialSecurities),
    [initialSecurities],
  );

  const totalPortfolioPercentage = useMemo(
    () => sumPortfolioPercentages(initialSecurities, classTargetByProductId),
    [initialSecurities, classTargetByProductId],
  );

  // Per-group start index in the flat Draggable list (avoid mutating a counter during render).
  const draggableOffsets = useMemo(() => {
    if (!groupedByAssetClass) return [];
    let offset = 0;
    return groupedByAssetClass.map(({ securities: gs }) => {
      const start = offset;
      offset += gs.length;
      return start;
    });
  }, [groupedByAssetClass]);

  /**
   * Every row on screen, whichever mode renders it, so the chips differ from
   * one another and match the donut deriving the same set alongside.
   */
  const assetClassesInView = useMemo(
    () =>
      [
        ...initialSecurities,
        ...(groupedByAssetClass?.flatMap((group) => group.securities) ?? []),
        ...unassignedSecurities,
      ].map((security) => security.financialProduct.primaryAssetClass),
    [initialSecurities, groupedByAssetClass, unassignedSecurities],
  );

  const { resolve: resolveAssetClassColor, loading: assetClassColorsLoading } =
    useAssetClassColors(assetClassesInView);

  const sharedSecurityRowProps = {
    isGroupedMode,
    editable,
    exceptTickers,
    options,
    securityEditingIndex,
    onEditSecurity: (index: number) => setSecurityEditingIndex(index),
    onUpdateSecurity: handleSecurityUpdate,
    taxRankEditingIndex,
    currentTaxRankValue,
    selectTaxRankRef,
    onTaxRankFocus: handleTaxRankFocus,
    onTaxRankBlur: handleTaxRankBlur,
    onTaxRankKeyDown: handleTaxRankKeyDown,
    onTaxRankValueChange: setCurrentTaxRankValue,
    targetPercentageEditingIndex,
    securitiesPercentage,
    selectTargetPercentageRef,
    onTargetPercentageFocus: handleTargetPercentageFocus,
    onTargetPercentageKeyDown: handleTargetPercentageKeyDown,
    onPercentageChange: (index: number, value: string) => {
      const newPercentage = [...securitiesStringPercentage];
      newPercentage[index] = value;
      setSecuritiesPercentage(newPercentage);
      handleTargetPercentageUpdate(index, Number(value));
    },
    portfolioPercentageEditingIndex,
    securitiesPortfolioPercentage,
    selectPortfolioPercentageRef,
    onPortfolioPercentageFocus: handlePortfolioPercentageFocus,
    onPortfolioPercentageKeyDown: handlePortfolioPercentageKeyDown,
    onPortfolioPercentageBlur: handlePortfolioPercentageBlur,
    onPortfolioPercentageChange: (index: number, value: string) => {
      setSecuritiesPortfolioPercentage((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
    },
    onRemoveSecurity,
    securitySelectAssetClassLevel,
    showPortfolioPercentageColumn,
  };

  const renderSecurityRow = (
    security: Security,
    index: number,
    dragProps?: DragProps,
  ): JSX.Element => (
    <SecurityRow
      key={security.financialProduct.id || `security-${index}`}
      security={security}
      index={index}
      dragProps={dragProps}
      assetClassColor={
        assetClassColorsLoading
          ? undefined
          : resolveAssetClassColor(security.financialProduct.primaryAssetClass)
      }
      {...sharedSecurityRowProps}
      classTargetPercentage={classTargetByProductId.get(
        security.financialProduct.id ?? '',
      )}
      reconciledPortfolioPercentage={reconciledPortfolioShareByProductId.get(
        security.financialProduct.id ?? '',
      )}
      allowEffectivePortfolioFallback={
        !isGroupedMode ||
        classTargetByProductId.has(security.financialProduct.id ?? '')
      }
    />
  );

  const renderTableBody = (
    provided: DroppableProvided,
    _snapshot: DroppableStateSnapshot,
  ) => (
    <TableBody {...provided.droppableProps} ref={provided.innerRef}>
      <TableHeaders
        options={options}
        sortField={sortField}
        sortDesc={sortDesc}
        onSort={handleSort}
        sortable={sortable}
        isGroupedMode={false}
      />
      {sortedSecurities.map((security, index) => {
        const isDragDisabled = !editable || security.financialProduct.isCash;
        return (
          <Draggable
            key={security.financialProduct.id || `security-${index}`}
            draggableId={security.financialProduct.id || `security-${index}`}
            index={index}
            isDragDisabled={isDragDisabled}
          >
            {(
              dragProvided: DraggableProvided,
              dragSnapshot: DraggableStateSnapshot,
            ) =>
              renderSecurityRow(security, index, {
                innerRef: dragProvided.innerRef,
                draggableProps: dragProvided.draggableProps,
                dragHandleProps: isDragDisabled
                  ? undefined
                  : dragProvided.dragHandleProps,
                rowHandleProps: isDragDisabled
                  ? dragProvided.dragHandleProps
                  : undefined,
                isDragging: dragSnapshot.isDragging,
              })
            }
          </Draggable>
        );
      })}
      {provided.placeholder}
      {editable && onAddSecurity && (
        <TableRow>
          <TableCell>
            <Typography
              variant='bodySmall'
              component='p'
              data-testid='add-security-button'
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
                color: 'primary.main',
                display: 'inline-block',
              }}
              onClick={handleAddSecurity}
            >
              {t('modelPortfolios:addSecurity')}
            </Typography>
          </TableCell>
          <TableCell colSpan={3} />
        </TableRow>
      )}
      <TableRow>
        <TableCell
          sx={{
            '& .MuiTypography-root': {
              fontSize: '14px',
              fontFamily: 'Interphases Pro',
              fontWeight: 500,
            },
            ...(ignoreBorderStyling
              ? { borderBottom: 'none' }
              : { borderBottomLeftRadius: '10px' }),
          }}
        >
          <strong>{t('components:portfolioTable.total')}</strong>
        </TableCell>
        <TableCell
          sx={{
            ...(ignoreBorderStyling ? { borderBottom: 'none' } : {}),
          }}
        />
        <TableCell
          sx={{
            ...(ignoreBorderStyling ? { borderBottom: 'none' } : {}),
          }}
        />
        <TableCell
          right
          sx={{
            '& .MuiTypography-root': {
              fontSize: '16px',
              fontFamily: 'Interphases Pro',
              fontWeight: 600,
            },
            pr: targetPercentagePaddingRight,
            ...(ignoreBorderStyling
              ? { borderBottom: 'none' }
              : { borderBottomRightRadius: '10px' }),
          }}
        >
          <Box
            sx={{
              color:
                (submitAttempted && totalPercentage !== 100) ||
                (!submitAttempted &&
                  totalPercentage !== 0 &&
                  totalPercentage !== 100)
                  ? 'error.main'
                  : 'inherit',
            }}
          >
            <strong>{`${totalPercentage.toFixed(2)}%`}</strong>
          </Box>
        </TableCell>
      </TableRow>
    </TableBody>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box
        sx={(theme) => ({
          // Only the widths a phone gives the table are short of what its
          // columns need, and only those scroll it sideways. Left on above
          // that it would cost the drag more than it buys the layout: asking
          // for overflow on one axis makes the box a scroll container on both,
          // and react-beautiful-dnd takes the nearest one as the droppable's
          // scroll parent. This box has no height to scroll, so it would stand
          // in front of the dialog that does and reordering would lose the
          // scroll it needs to reach the rest of the list.
          [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
          },
          '& .MuiTable-root': {
            width: '100%',
            minWidth: TABLE_MIN_WIDTH,
            tableLayout: 'fixed',
            overflow: 'visible',
          },
        })}
      >
        <Table aria-label='table'>
          {isGroupedMode && groupedByAssetClass ? (
            <GroupedTableBody
              groupedByAssetClass={groupedByAssetClass}
              unassignedSecurities={unassignedSecurities}
              initialSecurities={initialSecurities}
              draggableOffsets={draggableOffsets}
              editable={editable}
              cashAlreadyExists={cashAlreadyExists}
              options={options}
              sortField={sortField}
              sortDesc={sortDesc}
              onSort={handleSort}
              sortable={sortable}
              onAddSecurity={handleAddSecurity}
              onAddCash={onAddCash}
              renderSecurityRow={renderSecurityRow}
              showPortfolioPercentageColumn={showPortfolioPercentageColumn}
              totalPortfolioPercentage={totalPortfolioPercentage}
            />
          ) : (
            <Droppable droppableId='securities-table'>
              {(
                provided: DroppableProvided,
                snapshot: DroppableStateSnapshot,
              ) => renderTableBody(provided, snapshot)}
            </Droppable>
          )}
        </Table>
      </Box>
    </DragDropContext>
  );
};

export default SecuritiesSelectionTable;
